import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'hooks/Language';
import Select from 'react-select';

const languages = [
  { value: 'en', label: '🇬🇧 English' },
  { value: 'fr', label: '🇫🇷 French' },
  { value: 'de', label: '🇩🇪 German' },
  { value: 'es', label: '🇪🇸 Spanish' },
  { value: 'it', label: '🇮🇹 Italian' },
  { value: 'nl', label: '🇳🇱 Dutch' },
  { value: 'pl', label: '🇵🇱 Polish' },
  { value: 'pt', label: '🇵🇹 Portuguese' },
  { value: 'ru', label: '🇷🇺 Russian' },
  { value: 'sv', label: '🇸🇪 Swedish' },
  { value: 'da', label: '🇩🇰 Danish' },
  { value: 'fi', label: '🇫🇮 Finnish' },
  { value: 'no', label: '🇳🇴 Norwegian' },
  { value: 'el', label: '🇬🇷 Greek' },
  { value: 'cs', label: '🇨🇿 Czech' },
  { value: 'hu', label: '🇭🇺 Hungarian' },
  { value: 'ro', label: '🇷🇴 Romanian' },
  { value: 'bg', label: '🇧🇬 Bulgarian' },
  { value: 'hr', label: '🇭🇷 Croatian' },
  { value: 'sk', label: '🇸🇰 Slovak' },
  { value: 'lt', label: '🇱🇹 Lithuanian' },
  { value: 'sl', label: '🇸🇮 Slovenian' },
  { value: 'lv', label: '🇱🇻 Latvian' },
  { value: 'et', label: '🇪🇪 Estonian' },
  { value: 'ga', label: '🇮🇪 Irish' },
  { value: 'mt', label: '🇲🇹 Maltese' },
  { value: 'ta', label: '🇮🇳 Tamil' }
];

const currencies = [
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CHF', label: 'CHF (CHF)' },
  { value: 'PLN', label: 'PLN (zł)' },
  { value: 'SEK', label: 'SEK (kr)' },
  { value: 'NOK', label: 'NOK (kr)' },
  { value: 'DKK', label: 'DKK (kr)' },
  { value: 'CZK', label: 'CZK (Kč)' },
  { value: 'HUF', label: 'HUF (Ft)' },
  { value: 'RON', label: 'RON (lei)' },
  { value: 'BGN', label: 'BGN (лв)' },
  { value: 'ISK', label: 'ISK (kr)' },
];

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    minHeight: '20px',
    cursor: 'pointer',
    color: '#343a40'
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 2px',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    color: '#343a40'
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '20px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '0 2px',
    color: '#343a40',
    '&:hover': {
      color: '#000'
    },
    svg: {
      width: '12px',
      height: '12px'
    }
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#6c757d',
    fontWeight: 600,
    fontSize: '0.75rem' // xs size
  }),
  menu: (provided) => ({
    ...provided,
    minWidth: '130px',
    zIndex: 9999
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '0.75rem', // xs size
    padding: '4px 8px',
    cursor: 'pointer',
    textAlign: 'left'
  })
};

function UpperHeader() {
  const { translate, changeLanguage, language } = useTranslation();
  
  const [currency, setCurrency] = useState('CHF');

  useEffect(() => {
    const storedCurrency = localStorage.getItem('currency');
    if (storedCurrency) {
      setCurrency(storedCurrency);
    }
  }, []);

  const handleCurrencyChange = (selectedOption) => {
    const newCurrency = selectedOption.value;
    setCurrency(newCurrency);
    localStorage.setItem('currency', newCurrency);
  };

  const handleLanguageChange = (selectedOption) => {
    changeLanguage(selectedOption.value);
  };

  const currentLangOption = languages.find(lang => lang.value === language) || languages[0];
  const currentCurrencyOption = currencies.find(curr => curr.value === currency) || currencies[2];

  return (
    <div className={`upper-header pt-2 pb-2 d-none d-lg-block`}>
      <div className="container">
        <div className="row">
          <div className="col-lg-6">
            <ul className="nav">
              <li className="nav-item">
                <Link className=" ps-0" to="/about">
                  {translate('about')}
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/account/orders">
                  {translate('myAccount')}
                </Link>
              </li>
              <li className="nav-item">
                <Link to="#">Wishlist</Link>
              </li>
              <li className="nav-item">
                <Link to="/account/tracking">Order Tracking</Link>
              </li>
            </ul>
          </div>
          <div className="col-lg-6 text-end">
            <ul className="navbar-nav float-end d-flex flex-row align-items-center">
              <li className="nav-item me-2">
                <Select
                  value={currentLangOption}
                  onChange={handleLanguageChange}
                  options={languages}
                  styles={customSelectStyles}
                  isSearchable={true}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </li>
              <li className="nav-item">
                <Select
                  value={currentCurrencyOption}
                  onChange={handleCurrencyChange}
                  options={currencies}
                  styles={customSelectStyles}
                  isSearchable={true}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpperHeader;
